import * as React from 'react';
import BPromise from 'bluebird';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { Loader } from 'Src/components/Loader/Loader';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import { Grid2 } from 'Src/components/Grid/Grid2';
import {
  getCreatedAtWithTime,
  getMessageActionStatus,
  getMessageInvitationStatus,
  getMessageTakePart,
  getMessageTypeByKind
} from 'Src/helpers/accessor/accessor';
import { MessageDefinition } from 'Src/types/message';
import {
  getParentAllArchiveMessages,
  getParentArchiveMessages,
  getParentArchiveMessagesCount
} from 'Src/helpers/service/parent/messagesTable';
import { getChildrenEvent } from '../../../../helpers/service/parent/childrenEvents';
import { getSelectOptionsForMessageActionStatus, getSelectOptionsForMessageKindByType } from 'Src/helpers/table/select';
import { ResizablePanel } from 'Src/components/ResizablePanel/ResizablePanel';
import { MESSAGE_TABS } from 'Src/consts/message';
import { MessageArchiveSummary } from 'Src/components/MessageSummary/MessageArchiveSummary';
import { GenericSummary } from 'Src/components/GenericSummary/GenericSummary';
import { MessageAdditionalSummary } from 'Src/components/MessageSummary/MessageAdditionalSummary';
import { SchoolEvent } from '../../../../models/event';
import { getEventNameForParent } from '../../../../helpers/accessor/accessor';
import { getChildren } from '../../../../helpers/service/parent/children';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Message type',
    field: 'messageType',
    isSort: false,
    type: FILTER_TYPE.SELECT,
    accessor: getMessageTypeByKind
  },
  {
    text: 'Student name',
    field: 'studentFirstName',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['playerDetailsData', 'firstName']
  },
  {
    text: 'Student surname',
    field: 'studentLastName',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['playerDetailsData', 'lastName']
  },
  {
    text: 'Created at',
    field: 'createdAt',
    isSort: false,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getCreatedAtWithTime
  },
  {
    text: 'Status',
    field: 'messageInvitationStatus',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getMessageInvitationStatus
  },
  {
    text: 'Status authorize',
    field: 'messageActionStatus',
    isSort: false,
    type: FILTER_TYPE.SELECT,
    accessor: getMessageActionStatus
  },
  {
    text: 'Take part',
    field: 'messageTakePart',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getMessageTakePart
  }
];

interface State {
  items: MessageDefinition[];
  currentPage: number;
  selectedItems: MessageDefinition[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  userTags: any[];
  total: number;
  basePath: string;
  schoolEvent: SchoolEvent;
  childIdList: string[];
  currentTabIndex: number;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class ParentMessagesArchive extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      userTags: [],
      total: 0,
      basePath: '',
      schoolEvent: undefined,
      childIdList: [],
      currentTabIndex: 0
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true
      });
      this.setItems();
    }
  }

  setItems() {
    const { history, user } = this.props;

    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(COLUMNS, search);

    const isShowFilter = isFilterExist2(filters);
    const isDataFiltered = isFilterExist2(filters);

    const where = getServerFieldSectionWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const getItemsPromise = getParentArchiveMessages(user, serverQueryFilter);
    const getItemsCountPromise = getParentArchiveMessagesCount(user, where);
    const getChildrenPromise = getChildren(user);

    const promises = [getItemsCountPromise, getItemsPromise, getChildrenPromise];

    return BPromise.all(promises).then(([countObj, items, children]) => {
      const childIdList = children.map(child => child.id);
      this.setState({
        currentPage: page,
        sortDirection: sortDirection,
        sortColumnsName: sortColumnsName,
        isShowFilter: isShowFilter,
        isDataFiltered: isDataFiltered,
        filters: filters,
        items: items,
        total: countObj.count,
        basePath: history.location.pathname,
        isLoading: false,
        childIdList
      });

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { user } = this.props;
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    const message = selectedItemsUpdated[0];
    const eventId = propz.get(message, ['eventId']);

    if (selectedItemsUpdated.length === 1 && typeof eventId !== 'undefined') {
      this.setState({
        isLoading: true
      });

      getChildrenEvent(user, eventId).then(schoolEvent => {
        this.setState({
          schoolEvent,
          isLoading: false,
          selectedItems: selectedItemsUpdated
        });
      });
    } else {
      this.setState({
        schoolEvent: undefined,
        selectedItems: selectedItemsUpdated
      });
    }
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true
    });

    const user = this.props.user;

    const where = getServerFieldSectionWhere(filters);

    getParentAllArchiveMessages(user, where).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const nextSelectedItems = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: nextSelectedItems,
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter
    });
  };

  renderMessageTable(): React.ReactNode {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      filters,
      isSelectAllChecked,
      currentPage,
      total
    } = this.state;

    const actionItems = [];

    const filterOptions = {
      messageType: getSelectOptionsForMessageKindByType(),
      messageActionStatus: getSelectOptionsForMessageActionStatus()
    };

    return (
      <Grid2
        dataItems={items}
        filters={filters}
        currentPage={currentPage}
        total={total}
        isSelectAllChecked={isSelectAllChecked}
        isDataFiltered={isDataFiltered}
        sortDirection={sortDirection}
        sortColumnsName={sortColumnsName}
        isShowFilter={isShowFilter}
        dataItemsSelected={selectedItems}
        columns={COLUMNS}
        actionItems={actionItems}
        options={filterOptions}
        onItemClick={this.onItemClick}
        onSortClick={this.onTableSortClick}
        onApplyFilterClick={this.onApplyFilterClick}
        onClearFilterClick={this.onClearFilterClick}
        onTableFilterChange={this.onTableFilterChange}
        onTableFilterClick={this.onTableFilterClick}
        setCurrentPageParams={this.setCurrentPageParams}
        onSelectAllClick={this.onSelectAllClick}
        onSelectAllOnPageClick={this.onSelectAllOnPageClick}
        onUnselectAllClick={this.onUnselectAllClick}
      />
    );
  }

  isOneItemSelected(): boolean {
    const selectedItems = this.state.selectedItems;
    return selectedItems.length === 1;
  }

  renderSummary(): React.ReactNode {
    const isOneItemSelected = this.isOneItemSelected();

    if (!isOneItemSelected) {
      return <div className="font-weight-bold">Select a message above</div>;
    }

    const { currentTabIndex } = this.state;
    const summaryTabs = this.getSummaryTabs();

    return (
      <GenericSummary onTabClick={this.onTabClick} tabs={MESSAGE_TABS} currentTabIndex={currentTabIndex}>
        {summaryTabs}
      </GenericSummary>
    );
  }

  onTabClick = (event, index: number) => {
    event.preventDefault();

    this.setState({
      currentTabIndex: index
    });
  };

  getSummaryTabs(): React.ReactNode {
    const { currentTabIndex, selectedItems, schoolEvent, childIdList } = this.state;
    const { user } = this.props;

    let eventName = '';
    if (typeof schoolEvent !== 'undefined') {
      eventName = getEventNameForParent(schoolEvent, childIdList);
    }

    const message = selectedItems[0];

    switch (currentTabIndex) {
      case 0:
        return <MessageArchiveSummary message={message} user={user} schoolEvent={schoolEvent} eventName={eventName} />;
      case 1:
        return <MessageAdditionalSummary message={message} />;
    }
  }

  render() {
    const { isLoading } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes = 'mt-3';

    return (
      <div className={classes}>
        {/*Place for render modal windows*/}
        <div className="row">
          <div className="col-md-12">
            <ResizablePanel>
              {this.renderMessageTable()}
              {this.renderSummary()}
            </ResizablePanel>
          </div>
        </div>
      </div>
    );
  }
}

